import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/lib/subscription";
import { useI18n } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MAX = 8;

export function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const navigate = useNavigate();
  const { refresh } = useSubscription();
  const { t } = useI18n();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempt = 0;
    const poll = async () => {
      attempt++;
      try {
        const { data } = await axios.get(`${API}/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
      } catch { /* ignore */ }
      if (attempt < POLL_MAX) {
        setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-zinc-100 px-6">
      <div className="max-w-md text-center rounded-2xl border border-zinc-800 bg-zinc-950/60 p-8">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-emerald-400 mx-auto animate-spin" />
            <h2 className="mt-4 text-xl font-bold">{t("confirming_payment") || "Confirming your payment…"}</h2>
            <p className="mt-2 text-zinc-400 text-sm">{t("please_wait") || "Please wait a moment."}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <h2 className="mt-4 text-2xl font-bold">{t("welcome_pro") || "Welcome to Pro!"}</h2>
            <p className="mt-2 text-zinc-400 text-sm">{t("sub_active") || "Your subscription is active."}</p>
            <Button data-testid="billing-continue-btn" onClick={() => navigate("/")} className="mt-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-6">
              {t("go_dashboard") || "Go to dashboard"}
            </Button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="w-10 h-10 text-red-400 mx-auto" />
            <h2 className="mt-4 text-xl font-bold">{t("payment_pending") || "Payment still processing"}</h2>
            <p className="mt-2 text-zinc-400 text-sm">{t("check_later") || "It may take a minute. You can check your subscription later."}</p>
            <Button onClick={() => navigate("/")} className="mt-6 rounded-full">
              {t("continue") || "Continue"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function BillingCancel() {
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-zinc-100 px-6">
      <div className="max-w-md text-center rounded-2xl border border-zinc-800 bg-zinc-950/60 p-8">
        <XCircle className="w-10 h-10 text-zinc-500 mx-auto" />
        <h2 className="mt-4 text-xl font-bold">{t("checkout_cancelled") || "Checkout cancelled"}</h2>
        <p className="mt-2 text-zinc-400 text-sm">{t("no_charge") || "No worries — you weren't charged."}</p>
        <Button data-testid="billing-back-btn" onClick={() => navigate("/")} className="mt-6 rounded-full bg-white text-black hover:bg-zinc-200">
          {t("back") || "Back"}
        </Button>
      </div>
    </div>
  );
}
